// React
import { useEffect, useRef, useState, useLayoutEffect } from "react";

// External libraries
import useResizeObserver from "use-resize-observer";

function getBaseOverflowContext(size) {
    if (size < 1) return [];
    return new Array(size).fill(null).map(() => ({ overflow: false, overflowWidth: 0 }));
}

export const OverflowObserverCollapseDirection = {
    LeftToRight: 0,
    RightToLeft: 1
};

export function useOverflowObserver(contextSize, initializationOptions = {}) {
    const [overflowContext, setOverflowContext] = useState([]);
    const [initializeNewContext, setInitializeNewContext] = useState(false);

    const initializedRef = useRef(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (contextSize === 0) return;
        initializedRef.current = false;
        setOverflowContext(getBaseOverflowContext(contextSize));
        setInitializeNewContext(true);
    }, [contextSize]);

    useLayoutEffect(() => {
        if (!initializeNewContext) return;

        if (containerRef.current && !initializationOptions.Skip) {
            initializeOverflow();
        }

        setInitializeNewContext(false);
        initializedRef.current = true;
    }, [initializeNewContext]);

    const initializeOverflow = () => {
        const container = containerRef.current;
        const containerWidth = container.offsetWidth;
        const scrollWidth = container.scrollWidth;
        let overflowDetected = container.scrollWidth > containerWidth;
        if (!overflowDetected) return;

        const { collapseDirection = OverflowObserverCollapseDirection.LeftToRight, paddingOffset = 0, indexOffset = 0 } = initializationOptions;
        const getIndex = (i, length) => collapseDirection === OverflowObserverCollapseDirection.LeftToRight ?
            getCollapseLeftToRightIndex(i, indexOffset) : getCollapseRightToLeftIndex(i, indexOffset, length)

        let overflowWidthOffset = -(paddingOffset) ?? 0;
        setOverflowContext(overflowContext.map((overflowItem, i) => {
            if (overflowDetected) {
                const childToMeasureIndex = getIndex(i, container.children.length);
                const handledChildWidth = container.children[childToMeasureIndex]?.offsetWidth;

                const newOverflowItem = {
                    overflow: true,
                    overflowWidth: scrollWidth - overflowWidthOffset + 8,
                };

                overflowWidthOffset += handledChildWidth - 1;
                overflowDetected = scrollWidth - overflowWidthOffset > containerWidth;
                return newOverflowItem;
            }

            return overflowItem;
        }));
    };

    const getCollapseLeftToRightIndex = (index, indexOffset) => {
        return index + indexOffset;
    };

    const getCollapseRightToLeftIndex = (index, indexOffset, length) => {
        return length - 1 - index - indexOffset;
    };

    const handleResize = () => {
        const container = containerRef.current;
        if (!container || !initializedRef.current) return;

        setOverflowContext(prev => {
            const containerWidth = container.offsetWidth;
            const containerScrollWidth = container.scrollWidth
            let overflowDetected = containerScrollWidth > containerWidth;

            return prev.map((overflowItem, i) => {
                if (overflowDetected && !overflowItem.overflow) {
                    overflowDetected = false;
                    return {
                        overflow: true,
                        overflowWidth: i !== 0 ? containerWidth : getInitialOverflowWidth(containerScrollWidth, containerWidth)
                    };
                }

                if (containerWidth > overflowItem.overflowWidth + 8 && overflowItem.overflow) { //8 supposed to protect against potential scroll bar not perfect and should be fixed
                    return {
                        overflow: false,
                        overflowWidth: 0
                    };
                }
                return overflowItem;
            });
        });
    };

    const getInitialOverflowWidth = (scrollWidth, containerWidth) => {
        return scrollWidth > containerWidth ? scrollWidth : containerWidth;
    };

    useResizeObserver({
        ref: containerRef,
        onResize: handleResize
    });

    const getOverflow = (index) => {
        return overflowContext[index]?.overflow;
    };

    return {
        overflowContext,
        containerRef,
        getOverflow
    };
}